import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { matchPath, withRouter } from "react-router-dom";
import { Col, Grid, Row } from "react-bootstrap";
import "./campaign_podcast_schedule.scss";
import get from "lodash/get";
import max from "lodash/max";
import mapValues from "lodash/mapValues";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import maxBy from "lodash/maxBy";
import {
  AverageCPM,
  EstimatedRunTime,
  Image,
  Impressions,
  SearchRow,
  TitleContainer,
  Total,
  useTriggerIf,
} from "../../lib/tables/campaign_show_list_table";
import CalendarInput from "../../lib/calendar-input";
import {
  getAverageCPMFromShow,
  getDurationInDays,
  getEstimatedImpressionsForShow,
} from "../../../lib/campaigns";
import Moment from "moment";
import { formatMoney } from "../../../lib/format-money";
import { MultiStepModal } from "../multi_step_modals/multi_step_modal";
import { getMyCards } from "../../../action_managers/subscriptions";
import {
  campaignOneWeek,
  editCampaign,
  getCampaign,
  sendCampaign,
  updateCampaignItems,
} from "../../../action_managers/campaigns";
import { oneLevelUp } from "../../lib/routing";
import { useGetCampaignAndShows } from "../../../hooks/campaigns";
import { fetchPublicShowsByUUIDs } from "../../../action_managers/public_show";
import PaymentConfirmation from "../../lib/payment_confirmation/payment_confirmation";
import { Elements } from "react-stripe-elements";
import StripeForm, { options } from "../../lib/stripe_form";
import { CalendarOverlay, useNumberOverlay } from "../../lib/tables/overlays";
import { hummanizeNumber } from "../../../lib/numbers";
import { UserContext } from "../../app";
import {
  CampaignStyleHostRead,
  CreditCardPaymentMethod,
  DistributionTypeDiscrete,
  InvoicingPaymentMethod,
  ProductExchangeTypeInstructions,
  ProductExchangeTypeMailing,
} from "../../../constants/campaigns";
import { If } from "react-extras";
import SuccessPage from "./success_page";
import { HttpError } from "../../../lib/http";
import { showWarning } from "../../../actions/app";
import { ChoosePaymentType, showPaymentType } from "./choose_payment_type";

const ScheduleShowRow = (campaign, budgets, startDate, onBudgetUpdate) => ({ show, uuid }) => {
  if (!show || !campaign) return null;

  const budget = get(budgets, uuid, 0);
  return (
    <SearchRow show={show} budget={budget} key={uuid}>
      <Image width={1} key={"image"} />
      <TitleContainer
        subTitle={`Weekly Downloads: ${hummanizeNumber(show.estimatedWeeklyDownloads)}`}
        width={2}
        key={"title"}
      />
      <AverageCPM width={2} key={"averageCPM"} campaign={campaign} />
      <Impressions
        campaign={campaign}
        width={3}
        key={"impressions"}
        onChange={impression => {
          const newBudget = (impression / 1000) * getAverageCPMFromShow(show, campaign);
          onBudgetUpdate({
            ...budgets,
            [uuid]: newBudget,
          });
        }}
      />
      <EstimatedRunTime
        campaign={campaign}
        width={3}
        startDate={startDate}
        key={"estimatedRunTime"}
        onChange={targetDate => {
          const budget = budgetFromDateDifference(campaign, show, startDate, targetDate);
          onBudgetUpdate({
            ...budgets,
            [uuid]: budget,
          });
        }}
      />
      <Total
        width={1}
        key={"total"}
        onChange={budget =>
          onBudgetUpdate({
            ...budgets,
            [uuid]: budget,
          })
        }
      />
    </SearchRow>
  );
};

const getDailyCost = (campaign, show) => {
  if (!campaign) return 0;
  return (getAverageCPMFromShow(show, campaign) * show.estimatedWeeklyDownloads) / 7 / 1000;
};

const budgetFromDateDifference = (campaign, show, startDate, targetDate) => {
  const dailyCost = getDailyCost(campaign, show);
  return Math.ceil(Moment(targetDate).diff(startDate, "hours") / 24) * dailyCost;
};

const getCampaignEndDate = (campaign, items, budgets, startDate) => {
  const maxDuration = max(
    items.map(({ uuid, show }) => getDurationInDays(campaign, show, budgets[uuid]))
  );
  return Moment(startDate).add(maxDuration, "days");
};

const initializeBudgetsFromCampaign = campaign =>
  getRelevantItems(campaign).reduce((agg, item) => ({ ...agg, [item.uuid]: item.totalBudget }), {});

const getRelevantItems = (campaign, publicShows) =>
  (campaign?.items ?? [])
    .filter(({ item }) => item.state === "draft")
    .map(({ item }) => ({ ...item, show: publicShows?.[item.showUUID] || {} })) ?? [];

const SchedulePodcast = props => {
  const {
    budgets,
    updateBudgets,
    startDate,
    updateStartDate,
    campaign,
    publicShows,
    totalCost = 0,
    validationErrors,
  } = props;

  const items = useMemo(() => getRelevantItems(campaign, publicShows), [campaign, publicShows]);

  const matchImpressionToBudget = useCallback(
    totalBudget => {
      if (!totalBudget) return;
      const eachBudget = totalBudget / items.length;
      updateBudgets(items.reduce((agg, { uuid, show }) => ({ ...agg, [uuid]: eachBudget }), {}));
    },
    [items, updateBudgets]
  );
  const campaignUUID = campaign?.uuid;

  const getMinumumStartDate = () => {
    const shouldHaveLongerDelay =
      campaign?.style === CampaignStyleHostRead &&
      (campaign?.productExchangeType === ProductExchangeTypeInstructions ||
        campaign?.productExchangeType === ProductExchangeTypeMailing);
    const minumumDelay = shouldHaveLongerDelay ? 21 : 7;
    // we add one hour because time elapses between setting this and submitting
    return new Moment().add(minumumDelay, "d").add(1, "h");
  };

  useEffect(() => {
    if (startDate.isBefore(getMinumumStartDate())) {
      updateStartDate(
        Moment.unix(
          Math.max.call(
            null,
            campaign?.startsAt,
            getMinumumStartDate().unix(),
            getMaxStartDate(items) ?? 0
          )
        )
      );
    }
    const firstTimeScheduling = items.every(item => item.totalBudget === 0);
    const budgetsInitialise = isEmpty(budgets);
    if ((campaign?.totalBudget ?? false) && firstTimeScheduling && budgetsInitialise) {
      matchImpressionToBudget(campaign.totalBudget);
    } else if (budgetsInitialise && !firstTimeScheduling && campaign) {
      updateBudgets(initializeBudgetsFromCampaign(campaign));
    }
  }, [
    campaignUUID,
    campaign,
    items,
    budgets,
    updateStartDate,
    matchImpressionToBudget,
    updateBudgets,
  ]);

  return (
    <Grid fluid className={"campaign-schedule-podcast"}>
      <Row>
        <Col xs={4} mdOffset={1}>
          <div className="small bold">WHEN WILL THIS PROMOTION RUN?</div>
          <CalendarInput
            input={{ value: startDate }}
            onChange={updateStartDate}
            noRange
            name={"startAt"}
            validationErrors={validationErrors}
            minDate={getMinumumStartDate().toDate()}
          />
        </Col>
      </Row>
      <Col xs={12} md={10} mdOffset={1}>
        <Row>
          <Col xs={3} xsOffset={7} mdOffset={8} className={"match-buttons"}>
            {useTriggerIf(
              true,
              ({ ref, onClick }) => (
                <div onClick={onClick} ref={ref} className="fake-link position-relative">
                  Match Dates
                </div>
              ),
              CalendarOverlay(
                getCampaignEndDate(campaign, items, budgets, startDate),
                targetDate => {
                  const newBudgets = items.reduce((agg, { uuid, show }) => {
                    agg[uuid] = budgetFromDateDifference(campaign, show, startDate, targetDate);
                    return agg;
                  }, {});
                  updateBudgets(newBudgets);
                },
                startDate
              )
            )}
          </Col>
          <Col xs={1} className={"match-buttons"}>
            {useTriggerIf(
              true,
              ({ ref, onClick }) => (
                <div onClick={onClick} ref={ref} className="fake-link position-relative">
                  Match Budget
                </div>
              ),
              useNumberOverlay({
                label: "TOTAL BUDGET",
                confirmText: "Balance Spend to Budget",
                onChange: v => matchImpressionToBudget(v * 100),
                prefix: "$",
                startValue: formatMoney(totalCost),
              })
            )}
          </Col>
        </Row>
      </Col>
      <Row>
        <Col xs={12} md={10} mdOffset={1}>
          {items.map(ScheduleShowRow(campaign, budgets, startDate, updateBudgets))}
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={10} mdOffset={1} className={"flex-row-container margin-top--medium"}>
          <div className={"bold"}>Estimated Downloads</div>
          <div className="flex-1" />
          <div>
            {items
              .reduce(
                (agg, { uuid, show }) =>
                  agg + getEstimatedImpressionsForShow(campaign, show, budgets[uuid]),
                0
              )
              .toLocaleString()}
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={10} mdOffset={1} className={"flex-row-container margin-top--medium"}>
          <div className={"bold"}>Date Range</div>
          <div className="flex-1" />
          <div>
            {Moment(startDate).format("MMMM Do, YYYY")} -{" "}
            {getCampaignEndDate(campaign, items, budgets, startDate).format("MMMM Do, YYYY")}
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={10} mdOffset={1} className={"flex-row-container"}>
          <h3>Total Cost:</h3>
          <div className="flex-1" />
          <h3>{formatMoney(totalCost)}</h3>
        </Col>
      </Row>
    </Grid>
  );
};

const getMaxStartDate = items => {
  const startAt = maxBy(items, "startAt")?.startAt;
  // zero value of startAt (ie. it's not been set) is 1/1/0001
  if (startAt < 0) {
    return;
  }
  return startAt;
};

const CampaignSchedulePodcast = props => {
  const { campaigns } = props;

  const dispatch = useDispatch();
  const [page2Ready, setPage2Ready] = useState(false);
  const [startDate, updateStartDate] = useState(Moment());
  const [oneWeekAmount, updateOneWeekAmount] = useState(0);
  const [budgets, updateBudgets] = useState({});
  const [token, updateToken] = useState();
  const [card, updateCard] = useState();
  const [pageNumber, updatePageNumber] = useState(0);
  const match = matchPath(window.location.pathname, "/campaigns/:campaignUUID/schedule");
  const campaignUUID = match?.params?.campaignUUID;
  const stripeFormRef = useRef(null);
  const user = useContext(UserContext);
  const campaign = campaigns?.[campaignUUID];
  const initialPaymentMethod = campaign?.paymentType;
  const invoicingEnabled = user?.invoicingPaymentEnabled ?? false;
  const [paymentMethodType, setPaymentMethodType] = useState(
    invoicingEnabled ? undefined : CreditCardPaymentMethod
  );

  useGetCampaignAndShows(props, campaignUUID);
  useEffect(() => {
    campaign?.paymentType && setPaymentMethodType(campaign.paymentType);
  }, [campaign?.paymentType]);
  useEffect(() => {
    dispatch(getMyCards());
  }, [dispatch]);

  // Discrete Campaigns can only pay via Invoicing
  useEffect(() => {
    if (campaign?.distributionType === DistributionTypeDiscrete) {
      setPaymentMethodType(InvoicingPaymentMethod);
    }
  }, [campaign, setPaymentMethodType]);

  const totalCost = Object.keys(budgets).reduce((agg, uuid) => agg + budgets[uuid], 0);

  const onSubmit = async () => {
    const resp = await props.sendCampaign(campaignUUID, card?.id, paymentMethodType);
    if (resp.status === 200) {
      props.getCampaign(campaignUUID);
    }
  };

  const handleCardSubmit = async (values = {}) => {
    if (!values.card) {
      values = await stripeFormRef.current.submit();
      if (values.error) {
        return values.error;
      }
    }
    let { card, token } = values;
    updateToken(token);
    updateCard(card);
    updatePageNumber(2);
  };

  const pages = [
    {
      Content: (
        <SchedulePodcast
          {...{
            budgets,
            updateBudgets,
            startDate,
            updateStartDate,
            campaign,
            totalCost,
            ...props,
          }}
        />
      ),
      onSubmit: async () => {
        if (isEmpty(budgets)) return;

        if (
          !isEqual(initializeBudgetsFromCampaign(campaign), budgets) ||
          startDate.unix() !== getMaxStartDate(getRelevantItems(campaign))
        ) {
          const resp = await props.updateCampaignItems(
            campaignUUID,
            mapValues(budgets, totalBudget => ({
              totalBudget: Math.round(totalBudget),
              startAt: Moment(startDate).unix(),
            }))
          );
          if (resp.status !== 200) {
            props.showWarning("Error Updating Shows");
            throw new HttpError("Something went wrong");
          }
        }
        if (Moment(startDate).unix() !== campaign.startsAt && campaign.state === "created") {
          let resp = await props.editCampaign({
            uuid: campaignUUID,
            startsAt: Moment(startDate).unix(),
          });
          if (resp.status !== 200) {
            props.showWarning("Error Updating Campaign");
            throw new HttpError("Something went wrong");
          }
        }
        let resp = await props.campaignOneWeek(campaignUUID);
        if (resp.status === 200) {
          await updateOneWeekAmount(resp.json.amount);
          //update campaignItems in the reducer
          await props.getCampaign(campaignUUID);
        } else {
          throw new HttpError("Something went wrong");
        }
      },
      title: "Scheduled Promotions",
    },
    {
      title: "Choose Payment Type",
      Content: (
        <ChoosePaymentType
          paymentMethodType={paymentMethodType}
          setPaymentMethodType={setPaymentMethodType}
        />
      ),
      hide: !showPaymentType(invoicingEnabled, initialPaymentMethod, campaign),
    },
    {
      Content: (
        <Grid fluid>
          <Row>
            <Col xs={12} md={6} mdOffset={3}>
              <Elements fonts={options.fonts}>
                <StripeForm
                  defaultSelectedCardID={card?.id}
                  ref={stripeFormRef}
                  newCardOnly={false}
                  onFormStatus={setPage2Ready}
                  onLoadingStatus={() => {}}
                  {...{
                    token,
                    card,
                    budgets,
                    updateBudgets,
                    getMyCards: props.getMyCards,
                    submit: handleCardSubmit,
                  }}
                />
              </Elements>
            </Col>
          </Row>
        </Grid>
      ),
      disabled: !page2Ready,
      title: "Payment",
      onSubmit: handleCardSubmit,
      hide: paymentMethodType === InvoicingPaymentMethod,
    },
    {
      Content: (
        <Row>
          <Col xs={12} md={8} mdOffset={2}>
            <PaymentConfirmation
              {...{
                paymentMethodType: paymentMethodType,
                brand: card?.brand,
                last4: card?.last4,
                amount: paymentMethodType === CreditCardPaymentMethod ? oneWeekAmount : totalCost,
                chargeTypeText:
                  paymentMethodType === CreditCardPaymentMethod
                    ? "One-Time Authorization"
                    : "Total Budget",
                additionalInfo: (
                  <>
                    <If condition={paymentMethodType === CreditCardPaymentMethod}>
                      <div className={"info margin-bottom--large"}>
                        We will hold these funds until the podcasters accept your offer and the
                        campaign starts. After the campaign starts, you will be billed incrementally
                        as the campaign runs, up to your maximum budget amount of{" "}
                        {formatMoney(
                          Object.entries(budgets).reduce((agg, [, amount]) => agg + amount, 0)
                        )}
                        .
                      </div>
                    </If>
                    <If condition={paymentMethodType === InvoicingPaymentMethod}>
                      <div className="info margin-bottom--large">
                        <If condition={!isEmpty(initialPaymentMethod)}>
                          <div>
                            You've selected <b>Pay by Invoice</b> for the entirety of this campaign.
                          </div>
                        </If>
                        <div>RedCircle will invoice you incrementally as the campaign runs.</div>
                        <div>Payment terms will be indicated on each invoice.</div>
                      </div>
                    </If>
                  </>
                ),
              }}
            />
          </Col>
        </Row>
      ),
      title: "Payment",
      onSubmit: onSubmit,
    },
    {
      title: "Success!",
      hideBack: true,
      submitText: "Done",
      Content: (
        <Row>
          <Col xs={12} md={8} mdOffset={2}>
            <SuccessPage />
          </Col>
        </Row>
      ),
    },
  ].map(setting => ({
    ...setting,
    isLoading: props.isLoading,
    showContent: !props.isLoading,
    isLarge: true,
  }));
  return (
    <MultiStepModal
      pageNumber={pageNumber}
      onUpdatePage={updatePageNumber}
      close={() => {
        props.closeModal();
        props.history.push(oneLevelUp(window.location));
      }}
      isLoading={props.campaignItemsIsLoading || props.campaignSendIsLoading}
      pages={pages}
      startingPage={0}
      modalProps={{ enforceFocus: false, autoFocus: false, restoreFocus: false }}
    />
  );
};

export default connect(
  state => ({
    campaigns: state.campaigns.campaigns,
    publicShows: state.publicShows,
    isLoading: state.campaigns.isLoading,
    campaignItemsIsLoading: state.campaignItems.isLoading,
    campaignSendIsLoading: state.campaigns.sendIsLoading,
    validationErrors: state.campaignItems.validationErrors,
  }),
  {
    getMyCards,
    getCampaign,
    editCampaign,
    sendCampaign,
    fetchPublicShowsByUUIDs,
    updateCampaignItems,
    campaignOneWeek,
    showWarning,
  }
)(withRouter(CampaignSchedulePodcast));
